import { Button } from 'components/Button/Button'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { slsClient } from 'clients/slsClient'
import { useDispatch, useSelector } from 'react-redux'
import { getUserData } from 'redux/reducers/users'
import { MySpinner } from 'components/MySpinner/MySpinner'
import { Logger } from 'utils/logger'
import { useMountedState } from 'hooks/useMountedState'
import {
  createSchema,
  DeleteAccountFormData
} from 'models/forms/DeleteAccountForm'
import { handleAccountLocked } from 'utils/handle-account-lockout'
import { useHistory } from 'react-router'
import { MyTextFieldPassword } from 'components/ReactHookForm/MyTextFieldPassword'
import { MyInternalLink, MyLink, MyLinkClickHandler } from 'components/MyLink'
import { medicareApiClient } from 'clients/medicareApiClient'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { Activity, Component, SubmissionType } from '@cms-max/medicare-api-sdk'
import { GenericError } from 'components/GenericError/GenericError'
import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import DeleteAccountApiErrorAlert from '../deleteAccount/DeleteAccountApiErrorAlert'
import { useEvent } from 'hooks/useEvent'

export type DeleteAccountFormProps = {
  apiError?: boolean
  onSubmit: (formData: DeleteAccountFormData) => void
  href?: string
  onClose?: () => void
}

export enum ReasonValues {
  NO_VALUE = '',
  ACCIDENT = "I didn't mean to create an account",
  TOO_MANY_EMAILS = 'I get too many emails',
  NOT_USEFUL = "I couldn't find what I needed",
  ACCOUNT_ISSUE = 'I have a problem with my account',
  OTHER = 'Other'
}

type VerifyPasswordResponseState =
  | 'initial'
  | 'loading'
  | 'locked'
  | 'success'
  | 'validation-error'
  | 'default-error'

const trackPasswordVerificationResponse = (
  respStatus: 'Failure' | 'Success'
) => {
  window.globalHeaderEvents?.handleEvent({
    core: {
      activity: Activity.DownstreamServiceResponse,
      property: 'MAX',
      component: Component.AccountSettings,
      elementId: 'accountsettings_verifypassword_response',
      additionalData: {
        result: respStatus
      },
      submissionType: SubmissionType.Verify
    }
  })
}

/**
 * @param props
 * @returns
 */
export const DeleteAccountForm = ({
  apiError,
  onSubmit,
  onClose,
  href
}: DeleteAccountFormProps) => {
  const { t } = useTranslation('deleteAccount')
  const { user } = useSelector(getUserData)
  const { fireEvent } = useEvent('accountSettings')

  const [displayEmailAlert, setDisplayEmailAlert] = useState(false)
  const [responseState, setResponseState] =
    useMountedState<VerifyPasswordResponseState>('initial')
  const [, setAttemptsRemaining] =
    useMountedState<string | undefined>(undefined)
  const alertContainerRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()
  const history = useHistory()
  const schema = useMemo(() => createSchema(t), [t])
  const defaultValues: Partial<DeleteAccountFormData> = {
    deleteConfirmation: String('no')
  }
  const methods = useForm<DeleteAccountFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: zodResolver(schema)
  })
  const watchReasonDropdown = methods.watch('reason')
  const watchConfirmationRadio = methods.watch('deleteConfirmation')

  const isUserConfirmed = watchConfirmationRadio === 'yes'

  useEffect(() => {
    const displayEmailAlertField =
      watchReasonDropdown === ReasonValues.TOO_MANY_EMAILS
    setDisplayEmailAlert(displayEmailAlertField)
  }, [watchReasonDropdown, watchConfirmationRadio, dispatch])

  const handleOnSubmit = useCallback(
    async (data: DeleteAccountFormData) => {
      const handleError = () => {
        alertContainerRef.current?.focus()
      }

      if (user?.username) {
        setResponseState('loading')
        // Default to 'default-error' to reduce if/else logic in the try/catch below
        // If verify password returns false, or we get an unexpect error thrown, we use default-error
        let responseState: VerifyPasswordResponseState = 'default-error'
        try {
          if (await slsClient.updateAccountCookie(data.password)) {
            responseState = 'success'
          }
        } catch (error: unknown) {
          Logger.error(error)
          // Handle attempts remaining
          if (error instanceof Error) {
            if (error.message === 'user is locked') {
              responseState = 'locked'
            } else if (
              error.message === 'incorrect password' ||
              error.message === 'missing password'
            ) {
              responseState = 'validation-error'
              const attemptsRemaining =
                await medicareApiClient.asyncGetPasswordAttemptsRemaining()
              setAttemptsRemaining(attemptsRemaining)

              methods.setError('password', {
                type: 'custom',
                message: t('content.invalidPassword.doesNotMatchOurRecords')
              })
            }
          }
        }

        // Update the actual component state
        setResponseState(responseState)

        // Trigger side effects - activity tracking, focusing page elements, going to the update modal, etc.
        if (responseState === 'locked') {
          trackPasswordVerificationResponse('Failure')
          await handleAccountLocked(history, dispatch)
        } else if (
          responseState === 'validation-error' ||
          responseState === 'default-error'
        ) {
          trackPasswordVerificationResponse('Failure')
          handleError()
        } else {
          trackPasswordVerificationResponse('Success')
          onSubmit(data)
        }
      } else {
        // This shouldn't happen because to get to account settings they need to be authenticated
        // Handled with the generic error
        setResponseState('default-error')
        handleError()
      }
    },
    [
      dispatch,
      history,
      onSubmit,
      setResponseState,
      setAttemptsRemaining,
      user?.username,
      methods,
      t
    ]
  )

  const handleOnSubmitNoConf: SubmitHandler<any> = async () => {
    history.push('/account-settings')
  }

  const handleClose: MyLinkClickHandler = ({ linkText, linkUrl }) => {
    fireEvent('accountsettings_deleteaccountcancel_click', {
      linkText,
      linkUrl
    })
    onClose?.()
  }

  return (
    <>
      <FormProvider {...methods}>
        <div ref={alertContainerRef} tabIndex={-1}>
          {responseState === 'default-error' && <GenericError />}
        </div>
        <form
          onSubmit={
            isUserConfirmed
              ? methods.handleSubmit(handleOnSubmit)
              : handleOnSubmitNoConf
          }
        >
          <MyChoiceList
            variableInputName="deleteConfirmation"
            label={<h2 className="ds-text-heading--xl">{t('content.confirmQuestion')}</h2>}
            errorMessageString=""
            choices={[
              {
                label: t('content.yesText'),
                value: 'yes'
              },
              {
                label: t('content.noText'),
                value: 'no'
              }
            ]}
          />
          {isUserConfirmed && (
            <>
              <p className="ds-u-margin-top--3">{t('content.requiredText')}</p>
              <div className="ds-u-margin-top--3">
                <MyTextFieldPassword
                  name="password"
                  label={t('content.passwordText')}
                  labelClassName="my-u-text-transform--default"
                  defaultValue=""
                  addErrorPrefix
                  aria-required
                />
                <MyDropdown
                  name="reason"
                  label={t('content.reasonText')}
                  className="my-c-claim-filter--field my-u-flex-1"
                  fieldClassName="ds-u-margin--0"
                  labelClassName="my-u-text-transform--default"
                  id="delete_account_reason"
                  addErrorPrefix
                  options={[
                    { value: ReasonValues.NO_VALUE, label: '' },
                    {
                      value: ReasonValues.ACCIDENT,
                      label: t('content.reasons.accident')
                    },
                    {
                      value: ReasonValues.TOO_MANY_EMAILS,
                      label: t('content.reasons.tooManyEmails')
                    },
                    {
                      value: ReasonValues.NOT_USEFUL,
                      label: t('content.reasons.notUseful')
                    },
                    {
                      value: ReasonValues.ACCOUNT_ISSUE,
                      label: t('content.reasons.accountIssue')
                    },
                    {
                      value: ReasonValues.OTHER,
                      label: t('content.reasons.other')
                    }
                  ]}
                  aria-required
                />

                {displayEmailAlert && (
                  <>
                    <Alert
                      heading={t('content.infoAlert.heading')}
                      className="ds-u-margin-top--4 ds-u-measure--wide"
                    >
                      <p>
                        {t('content.infoAlert.alertContent')}{' '}
                        <MyLink
                          href="https://public.govdelivery.com/accounts/USCMSMYMEDICARE/subscriber/new"
                          onClick={({ linkText, linkUrl }) =>
                            fireEvent(
                              'accountsettings_changeyouremailpreferences_click',
                              {
                                linkText,
                                linkUrl
                              }
                            )
                          }
                        >
                          {t('content.infoAlert.alertContentLink')}
                        </MyLink>
                      </p>
                    </Alert>
                  </>
                )}

                <MyTextField
                  name="additionalInfo"
                  rules={{
                    required: {
                      value: true,
                      message: t('incompleteInformation.repFullName')
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9 .'-]+$/,
                      message: t('invalidRegex.genericSpecialCharactersError')
                    }
                  }}
                  label={t('content.tellUsMoreText')}
                  labelClassName="my-u-text-transform--default"
                  maxLength={100}
                  addErrorPrefix
                  aria-required
                />

                {apiError && (
                  <DeleteAccountApiErrorAlert className="ds-u-margin-top--4 ds-u-measure--wide" />
                )}
              </div>
            </>
          )}
          <div className="ds-c-dialog__actions">
            <div className="ds-l-form-row">
              <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                <Button
                  type="submit"
                  className="ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full my-u-whitespace-nowrap"
                  disabled={!isUserConfirmed}
                  onClick={() =>
                    fireEvent('accountsettings_deleteaccountconfirm_click')
                  }
                >
                  {responseState === 'loading' && <MySpinner size="small" />}{' '}
                  <span className="notranslate">{t('content.submitText')}</span>
                </Button>
              </div>
              {(href || onClose) && (
                <div className="ds-l-col--12 ds-l-sm-col--auto my-u-my--2">
                  {href
                    ? (
                    <MyInternalLink
                      className="ds-c-button my-u-py--6 my-u-width--full"
                      to={href}
                      onClick={handleClose}
                    >
                      {t('content.cancelText')}
                    </MyInternalLink>
                      )
                    : (
                    <Button
                      className="ds-c-button my-u-py--6 my-u-width--full"
                      onClick={handleClose}
                    >
                      {t('content.cancelText')}
                    </Button>
                      )}
                </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  )
}
